import { FC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { InputNumber } from 'antd'
import {
  FormCard,
  FormItem,
  GroupedUsersMultiSelect,
  Input,
  MultiSelect,
  SelectAddOption,
} from '@lmc/ui-kit'
import { styled } from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { getRequiredRule } from 'screens/Rostering/utils'
import { cleanDecimalValues } from '../../formComponents/utils'
import { ShiftType } from '@lmc/ts-common'

type ShiftTypeValue = {
  id: string | undefined
  name: string
}

type ShiftFormCardProps = {
  shiftTypes?: ShiftType[]
  users?: any
  formData?: any
  form?: any
  setFormData: (data: any) => void
  isSubmitting?: boolean
}

const ShiftFormCard: FC<ShiftFormCardProps> = ({
  shiftTypes,
  users,
  formData,
  form,
  setFormData,
  isSubmitting = false,
}) => {
  const { t } = useTranslation()
  const [shiftTypeValue, setShiftTypeValue] = useState<
    ShiftTypeValue | undefined
  >(undefined)
  const [showValidation, setShowValidation] = useState(false)

  const shiftTypeOptions =
    shiftTypes?.map((shift) => ({
      value: {
        id: shift.id,
        name: shift.name,
      },
      label: shift.name,
    })) || []

  useEffect(() => {
    if (formData?.instanceTypeId && !shiftTypeValue) {
      const foundType = shiftTypeOptions.find(
        (option) => option.value.id === formData.instanceTypeId,
      )
      if (foundType) {
        setShiftTypeValue(foundType.value)
      }
    }
  }, [formData?.instanceTypeId, shiftTypeOptions, shiftTypeValue])

  useEffect(() => {
    if (isSubmitting) {
      setShowValidation(true)
    }
  }, [isSubmitting])

  useEffect(() => {
    if (shiftTypeValue) {
      setShowValidation(false)
    }
  }, [shiftTypeValue])

  const handleShiftTypeChange = (value: ShiftTypeValue) => {
    setShiftTypeValue(value)

    const isNewShiftType = !shiftTypeOptions.some(
      (option) =>
        option.value.id === value.id && option.value.name === value.name,
    )

    if (isNewShiftType) {
      setFormData((prev: any) => ({
        ...prev,
        newInstanceType: {
          name: value.name,
        },
        instanceTypeId: undefined,
      }))
    } else if (value && value.id) {
      setFormData((prev: any) => ({
        ...prev,
        instanceTypeId: value.id,
        newInstanceType: undefined,
      }))

      form?.setFieldValue('instanceTypeId', value.id)
    } else {
      setFormData((prev: any) => ({
        ...prev,
        instanceTypeId: undefined,
        newInstanceType: undefined,
      }))
    }
  }

  return (
    <FormCard level={2} padding="16px">
      <FormItem
        label={
          <MandatoryText>
            <RedText>*</RedText>
            <Text>{t('TYPE_OF_SHIFT')}</Text>
          </MandatoryText>
        }
        rules={[getRequiredRule(t('TYPE_OF_SHIFT'))]}
        validateStatus={showValidation && !shiftTypeValue ? 'error' : ''}
        help={
          showValidation && !shiftTypeValue
            ? t('TYPE_OF_SHIFT') + ' is required'
            : ''
        }
      >
        <SelectAddOption<ShiftTypeValue>
          options={shiftTypeOptions}
          selectedValue={shiftTypeValue}
          setSelectedValue={handleShiftTypeChange}
          valueKey="id"
          fields={[
            {
              key: 'name',
              placeholder: t('TYPE_NEW_SHIFT_TYPE'),
              type: 'text',
              size: 100,
            },
          ]}
        />
      </FormItem>

      <FormItem name="instanceTypeId" hidden>
        <input type="hidden" />
      </FormItem>

      <FormItem
        name="title"
        label={<Text>{t('TITLE')}</Text>}
        rules={[getRequiredRule(t('TITLE'))]}
      >
        <Input />
      </FormItem>
      <FormItem name="slotNotes" label={<Text>{t('DESCRIPTION')}</Text>}>
        <Input />
      </FormItem>
      <InlineFormItem>
        <NumberSupplyFormItem
          labelCol={labelColStyle}
          labelAlign="left"
          name="numberSupply"
          label={<Text>{t('TEAM_MEMBER(S)')}</Text>}
          rules={[getRequiredRule(t('TEAM_MEMBERS_REQUIRED'))]}
        >
          <InputNumber
            style={{ width: 72 }}
            min={1}
            max={50}
            type="number"
            formatter={cleanDecimalValues}
          />
        </NumberSupplyFormItem>
        <TeamMembersFormItem name="teamMembers">
          <GroupedUsersMultiSelect
            users={users}
            title={t('SELECT_TEAM_MEMBERS')}
            values={formData?.teamMembers || []}
            hasClear
            hasSearch
            hasSelectAll
            placeholder
            maxTagCount={0}
            suffixIcon={
              <Text>
                {formData?.teamMembers?.length ?? 0}/
                {formData?.numberSupply ?? 0}
              </Text>
            }
          />
        </TeamMembersFormItem>
      </InlineFormItem>
    </FormCard>
  )
}

const labelColStyle = {
  style: {
    width: 200,
  },
}

const NumberSupplyFormItem = styled(FormItem)`
  .ant-form-item-control {
    margin-bottom: 0 !important;
  }

  .ant-form-item-label > label {
    color: ${({ theme }) => theme.color.neutral.color4};
  }
  width: 200px;
`

const TeamMembersFormItem = styled(FormItem)`
  .ant-form-item-control {
    margin-bottom: 0 !important;
  }
  flex: 1;
  min-width: 0;
`

const InlineFormItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
`

const StyledMultiSelect = styled(MultiSelect)`
  min-width: 130px;
`
const MandatoryText = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const RedText = styled.span`
  color: red;
`

export default ShiftFormCard
